import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Header, Paragraph } from "components/atoms/Typography";
import { Button } from "components/atoms/Button";
import { Check, CloseButton, ErrorIcon } from "components/atoms/Icon";
import { useAccessToken } from "hooks/useAccessToken";
import getBulkEquipmentUploadStatus from "ApiLayer/Equipment/getBulkEquipmentUploadStatus";
import useBulkEquipmentUploadState from "recoilStore/useBulkEquipmentUploadState";
import "./BulkEquipmentUploadBanner.scss";

export function BulkEquipmentUploadBanner() {
    const { state } = useBulkEquipmentUploadState();
    if (!state.showBanner) return null;
    return createPortal(<Banner />, document.getElementById("root")!);
}

function Banner() {
    const { state, setState, resetStateToDefault } = useBulkEquipmentUploadState();
    const accessToken = useAccessToken();
    const intervalRef = useRef<NodeJS.Timer | null>(null);

    const progressPercent = state.total > 0 ? Math.round(((state.completed + state.failed) / state.total) * 100) : 0;

    useEffect(() => {
        if (state.batchId && state.status !== "completed") {
            intervalRef.current = setInterval(async () => {
                try {
                    const data = await getBulkEquipmentUploadStatus(state.batchId!, accessToken);
                    setState((prev) => ({
                        ...prev,
                        completed: data.completed,
                        failed: data.failed,
                        status: data.status,
                    }));
                    if (data.status === "completed") {
                        clearInterval(intervalRef.current!);
                    }
                } catch {
                    // keep polling on transient errors
                }
            }, 3000);
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [state.batchId, state.status]);

    if (state.status === "completed") {
        const allFailed = state.failed > 0 && state.completed === 0;
        return (
            <div className="bulk-upload-banner">
                <div className="bulk-upload-banner__header">
                    {allFailed ? <ErrorIcon /> : <Check />}
                    <Header
                        size="x-small"
                        style={{
                            color: allFailed ? undefined : "var(--audette-content-positive)",
                        }}
                    >
                        Bulk upload complete
                    </Header>
                    <CloseButton onClick={resetStateToDefault} />
                </div>
                <div className="bulk-upload-banner__counts">
                    <Paragraph size="small" style={{ color: "var(--audette-content-positive)" }}>
                        {state.completed} succeeded
                    </Paragraph>
                    {state.failed > 0 && (
                        <Paragraph size="small" style={{ color: "var(--audette-content-negative)" }}>
                            {state.failed} failed
                        </Paragraph>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="bulk-upload-banner">
            <div className="bulk-upload-banner__header">
                <Header size="x-small">Processing buildings…</Header>
                <CloseButton onClick={resetStateToDefault} />
            </div>
            <div className="bulk-upload-banner__counts">
                <Paragraph size="small" style={{ color: "var(--audette-gray-500)" }}>
                    {state.completed + state.failed} / {state.total}
                </Paragraph>
                <Button onClick={resetStateToDefault} type="link">
                    Dismiss
                </Button>
            </div>
            <div className="bulk-upload-loading-bar-container">
                <div className="bulk-upload-loading-bar" style={{ width: `${progressPercent}%` }} />
            </div>
        </div>
    );
}
